import {
  Card,
  ErrorBox,
  Field,
  Input,
  LineChart,
  Link,
  Loading,
  Page,
  Select,
  Table,
  Td,
} from '@elsa/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { fmtLei, fmtMonth, fmtNum } from '../lib/format.ts';
import { rpc } from '../lib/supabase.ts';
import type { PriceRow } from '../lib/types.ts';
import { Results } from './Search.tsx';

// Preturile pe cod CPV (seap.price_stats: min / p25 / median / p75 / max pe luna si unitate de
// masura, reimprospatate zilnic), pe istoricul permis de plan.

export function PricesPage() {
  const [q, setQ] = useState('');
  const codes = useQuery({
    queryKey: ['search_cpv', q],
    enabled: q.trim().length >= 2,
    queryFn: () => rpc<{ code: string; label: string | null }[]>('search_cpv', { p_q: q.trim() }),
  });
  return (
    <Page title="Prețuri pe cod CPV">
      <Card>
        <Field label="Codul CPV sau denumirea" help="ex. 33141310 sau „seringi”">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Caută un cod CPV"
            autoFocus
          />
        </Field>
      </Card>
      {codes.data ? (
        <Card title="Coduri">
          <Table head={['Cod', 'Denumire']} empty={!codes.data.length}>
            {codes.data.map((c) => (
              <tr key={c.code}>
                <Td className="whitespace-nowrap">
                  <Link to={`/app/preturi/${c.code}`} className="font-medium hover:underline">
                    {c.code}
                  </Link>
                </Td>
                <Td>{c.label}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      ) : null}
      <ErrorBox error={codes.error} />
    </Page>
  );
}

export function CpvPage({ code }: { code: string }) {
  const q = useQuery({
    queryKey: ['cpv', code],
    queryFn: () =>
      rpc<{ code: string; label: string | null; prices: PriceRow[] } | null>('cpv', {
        p_code: code,
      }),
  });
  const units = useMemo(() => {
    const by = new Map<string, number>();
    for (const r of q.data?.prices ?? [])
      by.set(r.measure_unit, (by.get(r.measure_unit) ?? 0) + Number(r.samples));
    return [...by.entries()].sort((a, b) => b[1] - a[1]).map(([u]) => u);
  }, [q.data]);
  const [unit, setUnit] = useState<string | null>(null);
  const um = unit ?? units[0] ?? null;
  const rows = (q.data?.prices ?? []).filter((r) => r.measure_unit === um);
  const series = [
    {
      name: 'mediana',
      color: '#444141',
      points: rows.map(
        (r) => [Date.parse(r.month) / 1000, Number(r.price_median)] as [number, number],
      ),
    },
    {
      name: 'p25',
      color: '#9b9797',
      dashed: true,
      points: rows.map(
        (r) => [Date.parse(r.month) / 1000, Number(r.price_p25)] as [number, number],
      ),
    },
    {
      name: 'p75',
      color: '#9b9797',
      dashed: true,
      points: rows.map(
        (r) => [Date.parse(r.month) / 1000, Number(r.price_p75)] as [number, number],
      ),
    },
  ];

  if (q.isLoading) return <Page title={code}>{<Loading />}</Page>;
  if (q.error) return <Page title={code}>{<ErrorBox error={q.error} />}</Page>;
  if (!q.data) return <Page title={code}>Codul CPV nu există în nomenclator.</Page>;
  return (
    <Page title={`${code} ${q.data.label ?? ''}`}>
      {!units.length ? (
        <p className="text-sm text-gri-800">
          Nu există încă prețuri pentru acest cod în istoricul planului tău. Statisticile se
          recalculează zilnic, după extragerile de noapte.
        </p>
      ) : (
        <Card
          title="Prețul unitar pe lună (lei)"
          actions={
            units.length > 1 ? (
              <Select
                aria-label="Unitatea de măsură"
                value={um ?? ''}
                onChange={(e) => setUnit(e.target.value)}
                className="w-auto"
              >
                {units.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Select>
            ) : (
              <span className="text-xs text-gri-700">UM: {um}</span>
            )
          }
        >
          {rows.length > 1 ? (
            <LineChart
              series={series}
              unit=" lei"
              formatX={(t) => fmtMonth(new Date(t * 1000).toISOString())}
            />
          ) : null}
          <Table
            head={['Luna', 'Mostre', 'Furnizori', 'Minim', 'Mediana', 'Maxim', 'Interval 25–75%']}
          >
            {rows.map((r) => (
              <tr key={r.month}>
                <Td className="whitespace-nowrap">{fmtMonth(r.month)}</Td>
                <Td className="tabular-nums">{fmtNum(r.samples)}</Td>
                <Td className="tabular-nums">{fmtNum(r.suppliers)}</Td>
                <Td className="tabular-nums">{fmtLei(r.price_min)}</Td>
                <Td className="font-medium tabular-nums">{fmtLei(r.price_median)}</Td>
                <Td className="tabular-nums">{fmtLei(r.price_max)}</Td>
                <Td className="whitespace-nowrap tabular-nums">
                  {fmtLei(r.price_p25)} – {fmtLei(r.price_p75)}
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
      <h2 className="eticheta pt-2 text-cerneala">Produsele cu acest cod</h2>
      <Results kind="item" params={{ cpv: code.split('-')[0] }} />
    </Page>
  );
}
